// biome-ignore-all lint/plugin/no-unlabeled-form-inputs: legacy unlabeled inputs, burn-down tracked in HTUI-392
import { useEffect, useMemo, type FC } from "react";

import {
  AddBoxIcon,
  Box,
  Column,
  Row,
  Select,
  Text,
  TimeIcon,
} from "@hightouch/htui";
import { v4 as uuidv4 } from "uuid";

import { isRealtimeEventModel } from "src/utils/event-models";
import { useFormErrorContext } from "src/contexts/form-error-context";
import {
  type EventCondition,
  type FunnelCondition,
  FunnelValueOptions,
  initialFunnelWindow,
  ConditionType,
  PropertyComparisonType,
  createPropertyCondition,
} from "src/types/visual";

import {
  createSubconditionUpdater,
  removeSubconditionMutative,
} from "src/components/explore/utils/condition-builders";
import { getManagedModelChannel } from "src/cep/utils";
import { formatSubconditions } from "src/components/explore/utils/format-subconditions";
import { isAndOrCondition } from "src/components/explore/utils/type-guards";
import { ConditionField, type FilterProps } from "./condition";
import { HStack } from "./shared";
import { AndOrToggleButton, XButton, DetailButton } from "./condition-buttons";
import { validateFunnelCondition } from "./condition-validation";
import { ErrorMessage } from "./error-message";
import { WindowFilter } from "./window-filter";
import { ComboboxFilter } from "./combobox-filter";
import { useFilterableColumnsQuery } from "src/graphql";
import { useFilterableOptionsContext } from "src/components/explore/filter-popover/filterable-options-hook";
import { FilterableOptionsProvider } from "src/components/explore/filter-popover/filterable-options-context";
import type { UpdaterEnforceDefined } from "src/utils/immutable-helpers";
import { createRelationshipOptionLabeler } from "src/utils/relationship-names";
import {
  getFunnelSubconditionFilterableSections,
  MESSAGE_EVENT_CHANNEL_ICON_OVERRIDES,
} from "src/components/explore/filter-popover/constants";

type FunnelFilterProps = Omit<FilterProps<FunnelCondition>, "onUpdate"> & {
  eventCondition: EventCondition;
  onUpdate: (updater: UpdaterEnforceDefined<FunnelCondition>) => void;
};

const performOptions = [
  { label: "Then did perform", value: true },
  { label: "Then did not perform", value: false },
];

export const FunnelFilter: FC<Readonly<FunnelFilterProps>> = (props) => {
  const {
    eventCondition,
    condition: unformattedCondition,
    onUpdate,
    onRemove,
  } = props;
  const filterId = useMemo<string>(uuidv4, []);

  // TODO: change this to a filter popover, and somehow
  // configure so that realtime events are disabled
  // Need to wrap parent with filterable options context
  // probably easier with functional breakup of useConditionSelect
  // Actually maybe we can just wrap it here since funnel filter is always single
  const { events: regularEvents, messageEvents } =
    useFilterableOptionsContext();

  const events = [...regularEvents, ...messageEvents].filter(
    // Realtime events are not supported for funnel conditions
    (event) => !isRealtimeEventModel(event.to_model.event),
  );

  // Wrap the subconditions in one and/or condition
  const condition = formatSubconditions(unformattedCondition);
  const topLevelSubcondition = condition.subconditions?.[0];
  // Assumption is that event condition will be formatted to always have one And/Or subcondition as a child (if there are subconditions)
  const subconditions = isAndOrCondition(topLevelSubcondition)
    ? topLevelSubcondition.conditions
    : [];

  const { getErrors, setFieldError, removeErrors } = useFormErrorContext();
  const filterErrors = getErrors(filterId);

  const eventError = filterErrors?.eventModelId;

  const funnelEventModelId = events?.find(
    ({ id }) => id.toString() === condition.relationshipId?.toString(),
  )?.to_model.id;

  // Fetch columns of the funnel condition's event model
  // These will be displayed in the property filter or the reference property filter
  const funnelEventFilterableColumns = useFilterableColumnsQuery(
    {
      segmentId: funnelEventModelId?.toString() ?? "",
    },
    {
      enabled: Boolean(funnelEventModelId),
      select: (data) => data.segments_by_pk?.filterable_audience_columns ?? [],
    },
  );

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    setFieldError(filterId, validateFunnelCondition(condition));

    return () => {
      removeErrors([filterId]);
    };
  }, [condition.eventModelId, filterId]);

  const eventName = events?.find(
    ({ id }) => id.toString() === eventCondition.relationshipId?.toString(),
  )?.to_model?.name;

  const getEventLabel = createRelationshipOptionLabeler(events);
  const eventOptions = events?.map((relationship) => {
    const {
      id: relationshipId,
      name: relationshipName,
      to_model: { id: eventModelId, cep_managed_model },
    } = relationship;
    const channel = getManagedModelChannel(cep_managed_model?.type);
    const channelIcon = channel
      ? MESSAGE_EVENT_CHANNEL_ICON_OVERRIDES[channel]
      : undefined;
    return {
      label: getEventLabel(relationship) || relationshipName,
      value: eventModelId?.toString(),
      relationshipId: relationshipId?.toString(),
      channelIcon,
    };
  });

  const updateWrappingConditionType = () => {
    if (!isAndOrCondition(topLevelSubcondition)) {
      return;
    }

    onUpdate((condition) => {
      condition.subconditions = [
        {
          type:
            topLevelSubcondition.type === ConditionType.And
              ? ConditionType.Or
              : ConditionType.And,
          id: topLevelSubcondition.id,
          conditions: topLevelSubcondition?.conditions ?? [],
        },
      ];
    });
  };

  const addSubcondition = () => {
    if (!isAndOrCondition(topLevelSubcondition)) {
      return;
    }

    onUpdate((condition) => {
      condition.subconditions = [
        {
          type: topLevelSubcondition.type,
          id: topLevelSubcondition.id,
          conditions: [
            ...topLevelSubcondition.conditions,
            createPropertyCondition(),
          ],
        },
      ];
    });
  };

  const removeSubcondition = (id?: string) => {
    onUpdate((condition) => removeSubconditionMutative(condition, id));
  };

  return (
    <>
      <HStack
        gap={2}
        sx={{
          alignItems: "flex-start",
          input: { fontWeight: "medium" },
          button: { fontWeight: "medium" },
        }}
      >
        <Row align="center" color="text.secondary" whiteSpace="nowrap">
          <Select
            isDisabled={props.isDisabled}
            options={performOptions}
            width="auto"
            value={condition.didPerform}
            onChange={(didPerform) =>
              onUpdate((condition) => {
                // @ts-expect-error TODO(nolan) Why is undefined a possible type here? Shouldn't it be impossible unless the `clearable` prop is added?
                condition.didPerform = didPerform;
              })
            }
          />
        </Row>

        <Column>
          <ComboboxFilter
            isDisabled={props.isDisabled}
            isInvalid={Boolean(eventError)}
            options={eventOptions}
            placeholder="Select an event"
            value={condition.eventModelId?.toString()}
            width="2xs"
            optionAccessories={(option) =>
              option.channelIcon
                ? {
                    leftAccessory: {
                      type: "icon-box",
                      icon: option.channelIcon.icon,
                      color: option.channelIcon.color,
                    },
                  }
                : undefined
            }
            onChange={(eventModelId) => {
              const event = eventOptions.find(
                ({ value }) => eventModelId === value,
              );

              if (!event) {
                return;
              }

              onUpdate((condition) => {
                condition.relationshipId = event.relationshipId;
                condition.eventModelId = eventModelId ?? null;
                condition.subconditions = [];
              });
            }}
          />
          {eventError && <ErrorMessage>{eventError}</ErrorMessage>}
        </Column>

        {onRemove && (
          <XButton isDisabled={props.isDisabled} onRemove={onRemove} />
        )}
      </HStack>
      {condition.window && (
        <WindowFilter
          isDisabled={props.isDisabled}
          condition={condition}
          eventName={eventName}
          onUpdate={onUpdate}
        />
      )}

      <FilterableOptionsProvider
        sections={getFunnelSubconditionFilterableSections()}
        modelId={funnelEventModelId}
      >
        {isAndOrCondition(topLevelSubcondition) &&
          subconditions?.map((subcondition, index) => {
            const updateSubcondition = createSubconditionUpdater(onUpdate)(
              subcondition.id,
            );
            return (
              <Row
                key={subcondition.id ?? index}
                sx={{
                  alignItems: "flex-start",
                  pl: 10,
                  button: { fontWeight: "medium" },
                }}
              >
                {index === 0 && (
                  <Box alignSelf="flex-start" mt={1.5} mr={2}>
                    <Text color="text.secondary">Where</Text>
                  </Box>
                )}
                {index !== 0 && (
                  <AndOrToggleButton
                    ml={-1}
                    isDisabled={props.isDisabled}
                    conditionType={topLevelSubcondition.type}
                    onClick={updateWrappingConditionType}
                  />
                )}
                <Select
                  isDisabled={props.isDisabled}
                  options={FunnelValueOptions}
                  mr={2}
                  width="auto"
                  value={subcondition.type}
                  onChange={(type) => {
                    if (
                      type !== ConditionType.Property &&
                      type !== ConditionType.ReferenceProperty
                    ) {
                      return;
                    }

                    updateSubcondition((condition) => {
                      // @ts-expect-error TODO(nolan): `value` We're missing referenced property event types somewhere here
                      condition.type = type;
                      condition.value = null;
                      // @ts-expect-error valueFromColumn doesn't exist on all the types here but we're clearing it
                      condition.valueFromColumn = null;
                    });
                  }}
                />
                {/* These are always going to be property filters. Overriding the `columns` won't get passed deeper than the property filter. */}
                <ConditionField
                  {...props}
                  propertyComparisonType={PropertyComparisonType.None}
                  lhsPropertyFilterVariant="dropdown"
                  modelId={eventCondition.eventModelId}
                  isLoading={funnelEventFilterableColumns.isLoading}
                  // TODO(nolan): replace with filter popover system
                  columns={funnelEventFilterableColumns.data ?? []}
                  condition={subcondition}
                  onUpdate={updateSubcondition}
                  onRemove={() => removeSubcondition(subcondition.id)}
                />
              </Row>
            );
          })}
      </FilterableOptionsProvider>
      <Row pl={10}>
        <DetailButton icon={AddBoxIcon} size="sm" onClick={addSubcondition}>
          Where event property is…
        </DetailButton>
        {!condition.window && (
          <DetailButton
            icon={TimeIcon}
            size="sm"
            onClick={() =>
              onUpdate((condition) => {
                condition.window = initialFunnelWindow;
              })
            }
          >
            Time window
          </DetailButton>
        )}
      </Row>
    </>
  );
};
